import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import { Dropdown, InputGroup } from 'react-bootstrap';
import { AppDispatch } from 'store/store';
import { getInterestRateListThunk } from 'actions/controlActions';
import { getFormattedRouteOneDealers, getIsRouteOneDealersLoading } from 'store/selectors/dealer.selector';
import { useUncontrolledInput } from 'modules/core/hooks';
import { IFormField } from 'modules/core/types/form';
import { useTranslation } from 'react-i18next';
import { IOption } from 'modules/core/types';
import { getUser } from 'store/selectors/auth.selector';

import {
  CustomDropdownDefaultToggle,
  CustomDropdownMenu,
  CustomDropdownSelectedScoreToggle,
  FormControl,
  OptionItem,
} from './styles';

export const DealerDropdown: FC<IFormField> = ({ name, validate, rules, changeCallback, disabled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { controlField, errorMessage, hasError, setValue, getValues } = useUncontrolledInput(name);
  const dealersList = useSelector(getFormattedRouteOneDealers);
  const isDealersLoading = useSelector(getIsRouteOneDealersLoading);
  const fieldValue = getValues()[name];
  const [selectedDealer, setSelectedDealer] = useState<IOption | undefined>(fieldValue);
  const isSelectedDefault = selectedDealer === undefined;
  const toggleTitle = isSelectedDefault
    ? t('components.lender.routeOne.dealerField.placeholder')
    : selectedDealer.title;
  const ToggleComponent = isSelectedDefault ? CustomDropdownDefaultToggle : CustomDropdownSelectedScoreToggle;

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState(dealersList);
  const user = useSelector(getUser);

  useEffect(() => {
    setSelectedDealer(fieldValue);
    setValue(name, fieldValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue?.value, fieldValue?.title]);

  useEffect(() => {
    if (filteredItems.length === 0 && dealersList.length > 0) {
      setFilteredItems(dealersList);
    }

    if (user?.dealer && dealersList.length > 0 && !selectedDealer) {
      const matchingDealer = dealersList.find((item) => item.title === user.dealer.name);

      if (matchingDealer) {
        setValue(name, matchingDealer);
        setSelectedDealer(matchingDealer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealersList, selectedDealer, user?.dealer]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);

      const value = e.target.value.toLowerCase();

      setFilteredItems(dealersList.filter((dealer) => dealer.title.toLowerCase().includes(value)));
    },
    [dealersList],
  );

  const renderOptions = useCallback(
    ({ onBlur }: ControllerRenderProps) =>
      filteredItems.map((dealer) => (
        <OptionItem
          onClick={(e: any) => {
            const nextValue = dealer;

            setValue(name, nextValue);
            setSelectedDealer(nextValue);

            if (fieldValue?.value !== nextValue.value && changeCallback) {
              changeCallback(e);
            }
          }}
          onBlur={onBlur}
          key={dealer.title}
          selected={selectedDealer?.title === dealer.title}
        >
          {dealer.title}
        </OptionItem>
      )),
    [filteredItems, name, selectedDealer?.title, fieldValue, setValue, changeCallback],
  );

  const renderDropdown = useCallback(
    (renderProps) => (
      <Dropdown style={{ position: 'unset' }} id={name} data-testid={name}>
        <ToggleComponent isInvalid={Boolean(errorMessage) || hasError} disabled={disabled || isDealersLoading}>
          {toggleTitle}
        </ToggleComponent>
        <CustomDropdownMenu>
          <InputGroup>
            <FormControl
              type="text"
              placeholder={t('common.buttons.search')}
              value={searchTerm}
              onChange={handleSearch}
            />
          </InputGroup>
          {renderOptions(renderProps)}
        </CustomDropdownMenu>
      </Dropdown>
    ),
    [
      ToggleComponent,
      errorMessage,
      handleSearch,
      hasError,
      name,
      renderOptions,
      searchTerm,
      t,
      toggleTitle,
      disabled,
      isDealersLoading,
    ],
  );

  useEffect(() => {
    dispatch(getInterestRateListThunk());
  }, [dispatch]);

  return (
    <Controller
      name={name}
      render={renderDropdown}
      control={controlField}
      defaultValue={undefined}
      rules={{
        ...rules,
        validate,
      }}
    />
  );
};
